import React from 'react';
import { Box, Typography } from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import type { Message } from '../types/messages';
import { formatTimeKST } from '../../../utils/dateUtils';

interface MessageBubbleProps {
  message: Message;
  isOwn: boolean;
}

export default function MessageBubble({ message, isOwn }: MessageBubbleProps) {

  // 안전한 URL 링크 변환 함수
  const renderMessageContent = (content: string) => {
    const urlRegex = /(https?:\/\/[^\s<>"']+)/gi;
    const parts = content.split(urlRegex);

    return parts.map((part, i) => {
      // URL 패턴 매치 && http/https 프로토콜만 허용 (XSS 방어)
      if (urlRegex.test(part) && /^https?:\/\//i.test(part)) {
        return (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: isOwn ? '#C9A86C' : '#1A3461',
              textDecoration: 'underline',
              wordBreak: 'break-all'
            }}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  // 이모지만 있는 메시지 판별 (\p{Extended_Pictographic}으로 숫자/특수문자 오탐 방지)
  const isEmojiOnly = (text: string): boolean => {
    const trimmed = text.trim();
    if (trimmed.length === 0 || trimmed.length > 12) return false;
    const emojiOnlyRegex = /^[\p{Extended_Pictographic}\uFE0F\u200D\s]+$/u;
    return emojiOnlyRegex.test(trimmed);
  };

  const isLargeEmoji = isEmojiOnly(message.content);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        mb: 3,
        px: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isOwn ? 'row-reverse' : 'row',
          alignItems: 'flex-end',
          gap: 1,
          maxWidth: '75%',
        }}
      >
        {/* 메시지 버블 */}
        <Box
          sx={{
            bgcolor: isLargeEmoji ? 'transparent' : (isOwn ? '#0D1F3C' : '#F0EBE8'),
            color: isLargeEmoji ? 'inherit' : (isOwn ? 'white' : 'text.primary'),
            borderRadius: isLargeEmoji ? 0 : '18px',
            px: isLargeEmoji ? 0 : 2,
            py: isLargeEmoji ? 0 : 1.5,
            boxShadow: isLargeEmoji ? 'none' : (isOwn ? '0 1px 3px rgba(13,31,60,0.25)' : '0 1px 2px rgba(0,0,0,0.08)'),
            ...(isOwn && !isLargeEmoji && {
              borderBottomRightRadius: '4px',
            }),
            ...(!isOwn && !isLargeEmoji && {
              borderBottomLeftRadius: '4px',
            }),
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: isLargeEmoji ? '2.5rem' : '1rem',
              lineHeight: isLargeEmoji ? 1.2 : 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {renderMessageContent(message.content)}
          </Typography>
        </Box>

        {/* 시간 + 읽음 표시 (버블 바깥) */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isOwn ? 'row-reverse' : 'row',
            alignItems: 'center',
            gap: 0.5,
            minWidth: 'fit-content',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.75rem',
              color: 'text.secondary',
              whiteSpace: 'nowrap',
            }}
          >
            {formatTimeKST(message.createdAt)}
          </Typography>
          {isOwn && message.isRead && (
            <DoneAllIcon sx={{ fontSize: 14, color: 'primary.main' }} />
          )}
        </Box>
      </Box>
    </Box>
  );
}
